/**
 * Pick / GPU-stability diagnostics for the SplatDepthPicker revival.
 *
 * Chrome truncates WebGPU console warnings ("too many warnings, no more
 * warnings will be reported"), and a TDR kills the tab faster than anyone can
 * read a log — so every pick-related action is also recorded in a ring buffer
 * exposed at `window.__splatPickDiag`. After a crash or device loss, inspect:
 *
 *   __splatPickDiag.dump()      // prints the full trail
 *   __splatPickDiag.events      // raw ring buffer (newest last)
 *   __splatPickDiag.deviceLost  // populated if the GPU device was lost
 *
 * All console output is prefixed [SplatPickDiag]. This module is intentionally
 * dependency-free and cheap (no allocation beyond the event objects).
 */

export interface PickDiagEvent {
    t: number;              // performance.now() at the event
    wall: string;           // wall-clock ISO time (for cross-referencing crash reports)
    event: string;
    [key: string]: unknown;
}

const MAX_EVENTS = 300;

class PickDiagnostics {
    events: PickDiagEvent[] = [];
    deviceLost: { reason: string; message: string; at: string; trail: PickDiagEvent[] } | null = null;
    /** Per-event console.debug is OFF by default — the exact-splat pick runs on every reticle
     *  update in PUBLISHED scenes, and per-mousemove console output there is pure waste. The
     *  ring buffer always records; set `window.__splatPickDiag.verbose = true` to watch live. */
    verbose = false;

    push(event: string, data?: Record<string, unknown>): void {
        const e: PickDiagEvent = {
            t: Math.round(performance.now() * 10) / 10,
            wall: new Date().toISOString(),
            event,
            ...(data ?? {}),
        };
        this.events.push(e);
        if (this.events.length > MAX_EVENTS) this.events.splice(0, this.events.length - MAX_EVENTS);
        if (this.verbose) {
            // eslint-disable-next-line no-console
            console.debug(`[SplatPickDiag] ${event}`, data ?? '');
        }
    }

    /** Record a GPU device loss with the full breadcrumb trail frozen at that moment. */
    recordDeviceLost(reason: string, message: string): void {
        this.deviceLost = {
            reason,
            message,
            at: new Date().toISOString(),
            trail: this.events.slice(),
        };
        // eslint-disable-next-line no-console
        console.error(
            `[SplatPickDiag] ██ GPU DEVICE LOST ██ reason=${reason} message=${message}\n` +
            `Last ${Math.min(25, this.events.length)} actions before loss (full trail in __splatPickDiag.deviceLost.trail):`,
            this.events.slice(-25),
        );
    }

    dump(): void {
        // eslint-disable-next-line no-console
        console.log(`[SplatPickDiag] ${this.events.length} events`, this.events);
        if (this.deviceLost) {
            // eslint-disable-next-line no-console
            console.log('[SplatPickDiag] device loss recorded:', this.deviceLost);
        }
    }

    /** Wrap an async pick phase with timing + success/failure breadcrumbs. */
    async timed<T>(label: string, data: Record<string, unknown>, fn: () => Promise<T>): Promise<T> {
        const start = performance.now();
        this.push(`${label}:start`, data);
        try {
            const result = await fn();
            this.push(`${label}:done`, { ...data, ms: Math.round((performance.now() - start) * 10) / 10 });
            return result;
        } catch (err) {
            this.push(`${label}:ERROR`, { ...data, ms: Math.round((performance.now() - start) * 10) / 10, error: String(err) });
            throw err;
        }
    }
}

export const pickDiag = new PickDiagnostics();

// Expose for post-mortem inspection from the browser console.
declare global {
    interface Window { __splatPickDiag?: PickDiagnostics }
}
if (typeof window !== 'undefined') {
    window.__splatPickDiag = pickDiag;
}
